/*
 * pogi-sticky-atc
 * Shows the sticky bar once the buy box (data-target) scrolls out of view, and
 * HIDES it again as the footer approaches so it never covers the cross-sell /
 * footer (§ sticky-ATC redesign). The bar's button submits the native product
 * form via its [form=...] ref, so it adds the current variant + selling-plan
 * selection through Horizon's cart.
 */
class PogiStickyAtc extends HTMLElement {
  connectedCallback() {
    const selector = this.dataset.target || '.pogi-buy-box';
    const target = document.querySelector(selector);
    if (!target || !('IntersectionObserver' in window)) return;

    // i18n: JA override for the JS-rendered sold-out label, from the
    // [data-satc-i18n] blob the block renders via `| t`. EN fallback stays inline so
    // a missing key can never change the EN site.
    this.I = {};
    const i18nEl = this.querySelector('[data-satc-i18n]');
    if (i18nEl) { try { this.I = JSON.parse(i18nEl.textContent); } catch (e) { /* keep EN fallback */ } }
    this.t = (k, fb) => (this.I && this.I[k]) || (window.POGI_I18N && window.POGI_I18N[k]) || fb;

    this.pastBuyBox = false;
    this.buyBtn = target.querySelector('[data-pogi-atc]');
    this.buyLabel = target.querySelector('[data-pogi-atc-label]');
    this.stickyBtn = this.querySelector('.pogi-satc__btn');

    // Hide-anchors = the bottom-region bars the sticky must not overlap: the cross-sell's
    // OWN add bar AND the footer. It hides only while one of these is IN/approaching the
    // viewport → shows in the middle, fades at the cross-sell bar, REAPPEARS in the gap
    // after it, then fades again at the footer.
    this.stops = [
      document.querySelector('.pogi-routine__bar'),
      document.querySelector('[data-pogi-footer], .pogi-foot, footer'),
    ].filter(Boolean);

    // Appear once the photo GALLERY scrolls off the top (owner, 2026-06-12: while
    // picking variants, which sit ABOVE the ATC button, you could no longer see the
    // product — the old anchor was the ATC, so the bar never showed in exactly that
    // zone). Suppress while the REAL add-to-cart button is on screen so two CTAs
    // are never visible at once; the bar returns after the button passes.
    this.galleryPassed = false;
    this.atcVisible = false;
    const gallery = document.querySelector('media-gallery');
    const startEl = gallery || this.buyBtn || target;
    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.galleryPassed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        this.sync();
      },
      { threshold: 0 }
    );
    this.observer.observe(startEl);
    if (this.buyBtn) {
      this.atcObserver = new IntersectionObserver(
        ([entry]) => { this.atcVisible = entry.isIntersecting; this.sync(); },
        { threshold: 0 }
      );
      this.atcObserver.observe(this.buyBtn);
    }

    this.onScroll = () => this.sync();
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });

    // Track the live variant selection (same `variant:update` contract as the
    // cross-sell): swap the thumbnail + variant line so mobile shoppers can SEE
    // what they're selecting while the gallery is offscreen.
    this.productId = this.dataset.productId;
    try { this.imgs = JSON.parse(this.querySelector('[data-satc-imgs]').textContent) || {}; }
    catch (_) { this.imgs = {}; }
    this.onVariant = (e) => {
      const v = e.detail && e.detail.resource;
      const pid = e.detail && e.detail.data && e.detail.data.productId;
      if (!v || String(pid) !== String(this.productId)) return;
      const vt = this.querySelector('[data-satc-variant]');
      if (vt && v.title) vt.textContent = v.title.replace(/ \/ /g, ' · ');
      const im = this.querySelector('[data-satc-img]');
      const src = this.imgs[String(v.id)];
      if (im && src) im.src = src;
    };
    document.addEventListener('variant:update', this.onVariant);

    // Post-add feedback (§ "money moves" #3, 2026-06-12): after OUR button adds,
    // flash "✓ Added" for a beat, then restore. Armed only by a sticky-bar click
    // so buy-box adds don't trigger it; confirmed by the cart:update that follows.
    if (this.stickyBtn) {
      // POGI (2026-06-30): instant "Adding…" on click (matches the buy-box CTA), then
      // "✓ Added" on the confirming cart:update, then restore. Armed by a sticky click
      // so buy-box adds don't trigger it. Skips the false "✓ Added" on failed adds and
      // self-reverts if no cart:update confirms.
      this.stickyBtn.addEventListener('click', () => {
        this.addArmedAt = Date.now();
        if (this.addedHtml == null) this.addedHtml = this.stickyBtn.innerHTML; // capture original once
        clearTimeout(this.addedTimer);
        this.stickyBtn.setAttribute('aria-busy', 'true');
        this.stickyBtn.innerHTML = this.t('adding', 'Adding…');
        this.addedTimer = setTimeout(() => this.restoreStickyBtn(), 8000); // fallback
      });
      this.onCartUpdate = (e) => {
        if (!this.addArmedAt || Date.now() - this.addArmedAt > 8000) return;
        this.addArmedAt = 0;
        clearTimeout(this.addedTimer);
        const d = e && e.detail && e.detail.data;
        if (d && d.didError) { this.restoreStickyBtn(); return; } // failed/partial add -> no false success
        if (this.addedHtml == null) this.addedHtml = this.stickyBtn.innerHTML;
        this.stickyBtn.removeAttribute('aria-busy');
        this.stickyBtn.innerHTML = this.t('added', 'Added ✓');
        this.addedTimer = setTimeout(() => this.restoreStickyBtn(), 3200);
      };
      document.addEventListener('cart:update', this.onCartUpdate);
    }

    // Mirror the buy box's live ATC label (price + Subscribe/One-time) so the sticky
    // never shows a stale "From" price. Decoupled — reads the buy box's own rendered
    // output (works for all four buy boxes), and disables when the buy box is sold out.
    if (this.buyLabel) {
      this.priceMo = new MutationObserver(() => this.syncPrice());
      this.priceMo.observe(this.buyLabel, { childList: true, characterData: true, subtree: true });
      if (this.buyBtn) this.priceMo.observe(this.buyBtn, { attributes: true, attributeFilter: ['disabled'] });
      this.syncPrice();
    }
  }

  syncPrice() {
    if (!this.buyLabel) return;
    var label = (this.buyLabel.textContent || '').trim();        // "Start my routine · $17.99" | "Add to cart · $19.99" | "Sold out"
    var soldOut = (this.buyBtn && this.buyBtn.disabled) || /sold out/i.test(label);
    var idx = label.lastIndexOf('·');                          // split on the middle dot
    var price = idx >= 0 ? label.slice(idx + 1).trim() : '';
    var subscribe = /routine/i.test(label);
    // POGI: ignore a transient buy-box label ("Adding…" / "✓ Added" — no "·") so a
    // buy-box add doesn't momentarily flash the sticky bar to "Sold out".
    if (idx < 0 && !soldOut) return;
    var setAll = function (root, sel, txt) { root.querySelectorAll(sel).forEach(function (e) { e.textContent = txt; }); };
    var mobile = this.querySelector('[data-satc-mobile]');
    if (soldOut || !price) {
      var soldOutTxt = this.t('sold_out', 'Sold out');
      setAll(this, '[data-satc-price]', soldOutTxt);
      setAll(this, '[data-satc-mode]', '');
      if (mobile) mobile.textContent = soldOutTxt;
      if (this.stickyBtn) this.stickyBtn.disabled = true;
    } else {
      setAll(this, '[data-satc-price]', price);
      setAll(this, '[data-satc-mode]', subscribe ? this.t('mode_sub', 'with Subscribe & Save') : this.t('mode_onetime', 'One-time'));
      if (mobile) mobile.textContent = price + ' · ' + (subscribe ? 'Subscribe' : 'One-time');
      if (this.stickyBtn) this.stickyBtn.disabled = false;
    }
  }

  restoreStickyBtn() {
    if (!this.stickyBtn) return;
    clearTimeout(this.addedTimer);
    this.stickyBtn.removeAttribute('aria-busy');
    if (this.addedHtml != null) { this.stickyBtn.innerHTML = this.addedHtml; this.addedHtml = null; }
  }

  sync() {
    // Hide only while a conflicting bottom bar (cross-sell add bar / footer) is in or
    // approaching the viewport — NOT once it has scrolled fully ABOVE (top goes negative),
    // which was why it never came back after the cross-sell.
    const vh = window.innerHeight;
    const stopNear = this.stops.some((el) => {
      const r = el.getBoundingClientRect();
      return r.top < vh + 80 && r.bottom > 0;
    });
    this.classList.toggle('is-visible', this.galleryPassed && !this.atcVisible && !stopNear);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.atcObserver?.disconnect();
    this.priceMo?.disconnect();
    if (this.onVariant) document.removeEventListener('variant:update', this.onVariant);
    if (this.onCartUpdate) document.removeEventListener('cart:update', this.onCartUpdate);
    clearTimeout(this.addedTimer);
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
  }
}

if (!customElements.get('pogi-sticky-atc')) {
  customElements.define('pogi-sticky-atc', PogiStickyAtc);
}
